import { Typography } from 'components';
import { POLL_TIME, PROCESSING_PERCENTAGE_FULL } from 'constants/constants';
import { FileTypeEnum, UploadedFile } from 'gql/graphql';
import { usePollFileData } from 'providers/FileProviders/usePollFileData';
import { FC, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { FileDocsIcon } from 'lib/svgs/Icons';
import { processingFileFinished, setFileData, updateUploadingError } from './_actions';

interface IUploadProgressBar {
  file: UploadedFile;
  setFileData: (fileData: any) => void;
  processingFileFinished: (uid: string) => void;
}

const UploadProgressBar: FC<IUploadProgressBar> = ({
  file,
  setFileData,
  processingFileFinished
}) => {
  const dispatch = useDispatch();

  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(true);

  const { data: fileData, isFetched } = usePollFileData(
    {
      uids: [file.uid as string],
      fileType: FileTypeEnum.TranslationRequestFile
    },
    {
      enabled: !!file.uid && loading,
      staleTime: 0,

      refetchInterval: !!file.uid ? POLL_TIME : false
    }
  );

  const statusAndfileData = fileData?.at(0);

  const poolProcessing = useCallback(async () => {
    try {
      if (file.uid && attempt < 3 && isFetched) {
        if (statusAndfileData?.hasErrors)
          return dispatch(updateUploadingError(statusAndfileData?.errorMessage));

        if (
          statusAndfileData?.progress === PROCESSING_PERCENTAGE_FULL &&
          statusAndfileData?.fileData
        ) {
          setFileData(statusAndfileData?.fileData);
          processingFileFinished(statusAndfileData?.fileId as string);
          setLoading(false);
        }
      }
    } catch (error) {
      if (attempt >= 3) {
        return dispatch(updateUploadingError('Unexpected error'));
      }
      setAttempt((oldState) => oldState + 1);
    }
  }, [
    file.uid,
    attempt,
    isFetched,
    statusAndfileData?.hasErrors,
    statusAndfileData?.errorMessage,
    statusAndfileData?.progress,
    statusAndfileData?.fileData,
    statusAndfileData?.fileId,
    dispatch,
    setFileData,
    processingFileFinished
  ]);

  useEffect(() => {
    poolProcessing();
  }, [poolProcessing]);

  return (
    <div className="mt-4 flex flex-col gap-1 rounded-3xl border-1 border-tertiary-600 bg-tertiary-800 p-3">
      <div className="flex gap-2">
        <FileDocsIcon className="mt-1.5 scale-[1.3] !text-primary-blue-100" />
        <Typography variant="medium3" className="text-tertiary-100">
          {file.filename}
        </Typography>
      </div>
      <div className="animated_progress_bar">
        <span
          className="animated_progress"
          style={{ width: `${statusAndfileData?.progress ?? 0}%` }}
        >
          <span className="animated_stripes"></span>
        </span>
      </div>
    </div>
  );
};

export default connect(() => ({}), {
  setFileData,
  processingFileFinished
})(UploadProgressBar);
